import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { isValidTimestamp } from '@eluve/date-utils';
import {
  OutgoingEmailTemplateTypesLookup,
  appointmentFileTypesSchema,
  platformTypesSchema,
  supportedApiBasedVendorsSchema,
  supportedVendorsSchema,
} from '@eluve/graphql-types';

import {
  appointmentTagKeySchema,
  appointmentTagTypeSchema,
} from './appointment-tags';

const c = initContract();

const importAppointmentsRequestSchema = z.object({
  data: z.unknown(),
  vendor: supportedVendorsSchema,
  domain: z.string(),
  timezone: z.string().nullish(),
  externalPractitionerId: z.string().nullish(),
});

export type ImportAppointmentsRequest = z.infer<
  typeof importAppointmentsRequestSchema
>;

const prepareAppointmentFileUploadRequestSchema = z.object({
  uploadId: z.string(),
  extension: z.string(),
  fileType: appointmentFileTypesSchema,
  fileName: z.string(),
  fileSize: z.number(),
  fileHash: z.string(),
  origin: z.string().optional(),
  deviceId: z.string().uuid().optional(),
});

export type PrepareAppointmentFileUploadRequest = z.infer<
  typeof prepareAppointmentFileUploadRequestSchema
>;

const markFileUploadCompleteRequestSchema = z.object({
  uploadId: z.string().uuid(),
  shouldBeTranscribed: z.boolean(),
  recordingStartedAt: z.string().refine(
    (val) => {
      return isValidTimestamp(val);
    },
    {
      message: 'Invalid date format',
    },
  ),
  recordingDuration: z.number().nullish(),
  wasSegmentDegraded: z.boolean(),
  chunkNum: z.number().nullish(),
  // TODO(grigor)[ENG-1746]: Clean up legacy chunked audio duration code after full migration
  skipDurationTrackingInTranscripts: z.boolean().nullish(),
});

export type MarkFileUploadCompleteRequest = z.infer<
  typeof markFileUploadCompleteRequestSchema
>;

export const supportedPdfTypesSchema = z.enum(['CHART', 'SUPERBILL']);
export type SupportedPdfTypes = z.infer<typeof supportedPdfTypesSchema>;

export const supportedAppointmentEmailTypesSchema = z.nativeEnum(
  OutgoingEmailTemplateTypesLookup,
);
export type SupportedAppointmentEmailTypes = z.infer<
  typeof supportedAppointmentEmailTypesSchema
>;

export const bulkEntityFiltersSchema = z.object({
  locationId: z.array(z.string()).optional(),
  statusLabel: z.array(z.string()).optional(),
  userId: z.array(z.string()).optional(),
  startDateFrom: z.string().optional(), // "YYYY-MM-DD" format
  startDateTo: z.string().optional(), // "YYYY-MM-DD" format
  patientId: z.array(z.string()).optional(),
});
export type BulkEntityFilters = z.infer<typeof bulkEntityFiltersSchema>;

const BulkEntitySelectionModeSchema = z.enum(['EXPLICIT', 'FILTER']);
export const BulkEntitySelectionMode = BulkEntitySelectionModeSchema.enum;
export type BulkEntitySelectionModeType = z.infer<
  typeof BulkEntitySelectionModeSchema
>;

export const appointmentsContract = c.router(
  {
    getAppointments: {
      method: 'GET',
      path: '/',
      responses: {
        200: z.array(
          z.object({
            appointmentId: z.string(),
            externalAppointmentId: z.string().nullish(),
            name: z.string(),
            description: z.string().nullish(),
            startDate: z.date(),
            endDate: z.date().nullish(),
          }),
        ),
      },
      query: z.object({
        startDate: z.string(),
        endDate: z.string(),
        status: z
          .array(z.enum(['NOT_STARTED', 'COMPLETED', 'ACTIVE', 'CANCELLED']))
          .default(['NOT_STARTED']),
        vendor: supportedVendorsSchema.optional(),
        domain: z.string().optional(),
      }),
      summary: 'Get appointments',
    },
    syncToEhr: {
      method: 'POST',
      path: ':appointmentId/sync-to-ehr',
      responses: {
        201: z.object({
          chartId: z.string(),
          chartUrl: z.string(),
        }),
        422: z.object({
          error: z.string(),
        }),
      },
      body: z.object({
        vendor: supportedApiBasedVendorsSchema,
        domain: z.string(),
        platform: platformTypesSchema.default('WEB'),
      }),
      summary: 'Sync summary to an external EHR',
    },
    create: {
      method: 'POST',
      path: '',
      responses: {
        201: z.object({
          id: z.string(),
          magicLink: z.string(),
        }),
      },
      headers: z.object({
        authorization: z.string(),
      }),
      body: z.object({
        vendor: supportedVendorsSchema,
        user: z.unknown(),
        appointment: z.unknown(),
      }),
      summary: 'Create new or get existing appointment',
    },
    importAppointmentsFromApi: {
      method: 'POST',
      path: 'api-import',
      responses: {
        201: z.array(
          z.object({
            id: z.string(),
          }),
        ),
      },
      body: z.object({
        vendor: supportedApiBasedVendorsSchema,
        domain: z.string(),
        data: z.object({
          startDate: z.number(),
          endDate: z.number(),
        }),
      }),
      summary: 'Import appointments from vendor API',
    },
    importAppointments: {
      method: 'POST',
      path: 'import',
      responses: {
        201: z.array(
          z.object({
            id: z.string(),
          }),
        ),
      },
      body: importAppointmentsRequestSchema,
      summary: 'Import appointments',
    },
    getOrCreatePdfUrl: {
      method: 'POST',
      path: ':appointmentId/pdf',
      responses: {
        201: z.object({
          pdfUrl: z.string(),
        }),
      },
      body: z.object({
        type: supportedPdfTypesSchema,
      }),
      summary: 'Get or create a PDF URL for an appointment',
    },
    createBulkPdfZipUrl: {
      method: 'POST',
      path: 'pdf/bulk',
      responses: {
        201: z.object({
          zipUrl: z.string(),
          totalCount: z.number(),
          successCount: z.number(),
          failedCount: z.number(),
          failedAppointmentIds: z.array(z.string()),
        }),
        422: z.object({
          message: z.string(),
        }),
      },
      body: z
        .object({
          // default to explicit mode if not provided to keep backwards compatibility
          selectionMode: BulkEntitySelectionModeSchema.default(
            BulkEntitySelectionMode.EXPLICIT,
          ),
          appointmentIds: z.array(z.string()).min(1).max(100).optional(),
          filters: bulkEntityFiltersSchema.optional(),
          type: supportedPdfTypesSchema.extract([
            // we only support bulk export of patient chart PDFs (for now)
            supportedPdfTypesSchema.enum.CHART,
          ]),
        })
        .refine(
          (data) => {
            if (data.selectionMode === BulkEntitySelectionMode.EXPLICIT) {
              return (
                data.appointmentIds !== undefined &&
                data.appointmentIds.length > 0
              );
            } else if (data.selectionMode === BulkEntitySelectionMode.FILTER) {
              return (
                data.filters !== undefined &&
                Object.keys(data.filters).length > 0 &&
                Object.values(data.filters).some(
                  (value) =>
                    value !== undefined &&
                    value !== null &&
                    (!Array.isArray(value) || value.length > 0),
                )
              );
            }
            return false;
          },
          {
            message:
              'Must provide non-empty appointmentIds for explicit mode or non-empty filters for filter mode',
          },
        ),
      summary:
        'Create a zip file URL for bulk export of appointment chart PDFs',
    },
    /**
     * Prepare a file upload for an appointment by creating necessary DB records
     * to track the attempt and then returning a presigned upload URL
     */
    prepareAppointmentFileUpload: {
      method: 'POST',
      path: ':appointmentId/file-upload',
      body: prepareAppointmentFileUploadRequestSchema,
      responses: {
        201: z.object({
          uploadId: z.string(),
          gcsFilePath: z.string(),
          uploadURL: z.string(),
          bytesUploaded: z.number().optional(),
          status: z.enum(['FILE_UPLOAD_REQUIRED']),
        }),
        200: z.object({
          status: z.enum(['FILE_ALREADY_EXISTS']),
          uploadId: z.string(),
        }),
      },
    },
    /**
     * Mark a file upload that we previously prepped as complete once the
     * file has been succesfully uploaded to GCS
     */
    markFileUploadComplete: {
      method: 'POST',
      path: ':appointmentId/file-upload/complete',
      body: markFileUploadCompleteRequestSchema,
      responses: {
        200: z.object({}),
      },
    },
    prepareAppointmentAudioRecords: {
      method: 'POST',
      path: ':appointmentId/prepare-audio-records',
      body: z.object({
        fileName: z.string(),
        useResumableUploads: z.boolean(),
        origin: z.string().optional(),
        platform: platformTypesSchema.optional(),
        platformVersion: z.string().optional(),
        platformId: z.string().optional(),
        userAgent: z.string().optional(),
        microphoneName: z.string().optional(),
        metadata: z.record(z.string()).optional(),
        deviceId: z.string().uuid().optional(),
      }),
      responses: {
        200: z.object({
          resumableUploadSessionUri: z.string().optional(),
          nextChunkResumableUploadSessionUri: z.string().optional(),
        }),
      },
    },
    /**
     * Sets up an appointment for manual charting mode
     */
    startAppointmentInManualChartingMode: {
      method: 'POST',
      path: ':appointmentId/manual-mode',
      body: z.object({
        duplicatedAppointmentId: z.string().optional(),
        userLocale: z.string().optional(),
      }),
      responses: {
        200: z.object({}),
      },
    },
    /**
     * Generate billing code recommendations for an appointment in manual charting mode
     * using the latest human output instead of transcripts
     */
    generateBillingCodes: {
      method: 'POST',
      path: ':appointmentId/billing-codes/generate',
      body: z.object({}),
      responses: {
        200: z.object({}),
        422: z.object({
          message: z.string(),
        }),
      },
    },
    /**
     * Redirects to a presigned GCS Url for downloading an appointment file
     */
    downloadAppointmentFile: {
      method: 'GET',
      path: ':appointmentId/download/:fileId',
      responses: {
        302: z.void(),
        404: z.void(),
      },
    },
    addAppointmentTag: {
      method: 'POST',
      path: ':appointmentId/tags',
      responses: {
        200: z.object({}),
      },
      body: z.object({
        type: appointmentTagTypeSchema,
        key: appointmentTagKeySchema,
        fileId: z.string().uuid().optional(),
        metadata: z.record(z.unknown()).optional(),
      }),
      summary: 'Add a tag to an appointment',
    },
    createAppointment: {
      method: 'POST',
      path: 'new',
      responses: {
        201: z.object({
          id: z.string(),
          tenantId: z.string(),
          name: z.string().nullable(),
          locationId: z.string().nullable(),
        }),
        422: z.object({
          message: z.string(),
        }),
      },
      body: z.object({
        startDate: z.string().refine(isValidTimestamp, {
          message: 'Invalid date format',
        }),
        name: z.string().optional(),
        locationId: z.string().optional(),
      }),
      summary: 'Create a new appointment',
    },
    sendEmail: {
      method: 'POST',
      path: ':appointmentId/send-email',
      responses: {
        200: z.object({
          emailId: z.string(),
        }),
      },
      body: z.object({
        type: z.nativeEnum(OutgoingEmailTemplateTypesLookup),
        email: z.string().email(),
        subject: z.string(),
        message: z.string(),
      }),
      summary: 'Sends various appointment related emails to patient',
    },
    /**
     * List PENDING_UPLOAD user_uploads belonging to the calling user that are
     * attached to COMPLETED appointments past the safety age buffer. Used by
     * the periodic remote file sync machine to drive server-prompted orphan
     * reconciliation.
     */
    listOrphanedPendingUploads: {
      method: 'GET',
      path: 'orphan-candidates',
      query: z.object({
        deviceId: z.string().uuid().optional(),
      }),
      responses: {
        200: z.array(
          z.object({
            fileId: z.string().uuid(),
            appointmentId: z.string().uuid(),
            fileName: z.string(),
            matchedByDeviceId: z.boolean(),
          }),
        ),
      },
      summary:
        'List orphan PENDING_UPLOAD candidates on COMPLETED appointments',
    },
    /**
     * Cancel an orphan PENDING_UPLOAD user_upload. Idempotent: an already-CANCELLED
     * row also returns 200 with cancelled=true and wasAlreadyCancelled=true, so
     * callers can distinguish "I just flipped it" from "it was already cancelled."
     * Rejected with 409 when the row is in any non-pending, non-cancelled status.
     */
    cancelOrphanedFile: {
      method: 'POST',
      path: ':appointmentId/files/:fileId/cancel-orphaned',
      body: z.object({
        trigger: z.enum([
          'client_fail_fast',
          'server_prompted',
          'empty_segment',
        ]),
      }),
      responses: {
        200: z.object({
          cancelled: z.boolean(),
          wasAlreadyCancelled: z.boolean(),
          previousStatus: z.string(),
        }),
        404: z.object({ message: z.string() }),
        409: z.object({
          message: z.string(),
          currentStatus: z.string(),
        }),
      },
      summary: 'Cancel an orphan PENDING_UPLOAD user_upload',
    },
    /**
     * [ENG-4498] Reconcile a reserved-but-never-completed PENDING_UPLOAD slot
     * for a recording segment against GCS truth. Purely additive — it never
     * cancels an absent-object slot:
     *   - GCS object exists with usable bytes -> RECOVER (complete to AVAILABLE
     *     + create segment + schedule transcription), so a PUT that finalized
     *     before `markFileUploadComplete` was lost (and whose local file was
     *     later deleted) is never discarded.
     *   - GCS object exists but is zero/undersized -> CANCEL (existing
     *     zero-byte behavior inside markFileUploadComplete).
     *   - GCS object absent (never finalized — the empty-segment phantom) ->
     *     LEFT PENDING. The slot keeps blocking note generation, exactly as
     *     today; we deliberately do not guess-cancel missing audio.
     * Idempotent; only acts on PENDING_UPLOAD rows.
     */
    resolveReservedUpload: {
      method: 'POST',
      path: ':appointmentId/files/:fileId/resolve-reserved',
      // No body needed — the slot is identified by :fileId and all recovery
      // metadata is reconstructed server-side from the reserved file name.
      body: z.object({}),
      responses: {
        200: z.object({
          outcome: z.enum([
            // GCS had a finalized object with usable audio — completed.
            'recovered',
            // GCS object was zero/undersized — existing zero-byte cancel applied.
            'cancelled_zero_byte',
            // No GCS object exists — left PENDING_UPLOAD (still blocking).
            'left_pending',
            // Already AVAILABLE/CANCELLED on entry — nothing to do.
            'already_resolved',
          ]),
          previousStatus: z.string(),
        }),
        404: z.object({ message: z.string() }),
      },
      summary:
        'Reconcile a reserved PENDING_UPLOAD segment slot against GCS (recover finalized uploads; absent objects stay pending)',
    },
    retryProcessing: {
      method: 'POST',
      path: ':appointmentId/retry-processing',
      responses: {
        200: z.object({
          retriedTranscriptCount: z.number(),
          summarizationSeeded: z.boolean(),
        }),
        404: z.object({
          message: z.string(),
        }),
      },
      body: z.object({}),
      summary:
        'Retry any failed transcripts on this appointment and reseed summarization. Used when an appointment is stuck because a transcript failed and no LLM job was scheduled, or to recover from a failed note generation. Idempotent.',
    },
    /**
     * [ENG-4717] Explicit appointment completion. The client sends a manifest of
     * the audio files it actually produced for this appointment. The server:
     *   1. Cancels any non-terminal (PENDING_UPLOAD / PRE_PROVISIONED) audio slot
     *      NOT in the manifest — these are phantoms (the always-one-ahead
     *      pre-provisioned chunk, aborted recordings) that would otherwise block
     *      note generation forever.
     *   2. Computes `missingFiles`: manifest files the server does not yet have as
     *      AVAILABLE (still uploading). The client re-pushes these and shows
     *      "waiting on N uploads" UI; generation proceeds once they finalize.
     *   3. Marks the appointment COMPLETED, which fires the existing status trigger
     *      that schedules note generation.
     * The manifest is optional: an empty/absent manifest (rare cross-device case)
     * performs no cancellation and behaves exactly as the legacy mutation path.
     */
    completeAppointment: {
      method: 'POST',
      path: ':appointmentId/complete',
      body: z.object({
        userCompletedAt: z.string().refine(isValidTimestamp, {
          message: 'Invalid date format',
        }),
        manifest: z
          .object({
            files: z.array(
              z.object({
                fileName: z.string(),
                // Finalize-time diagnostics. byteSize === 0 marks a
                // finalized-empty file the server should cancel; the rest
                // (packetCount + write window) let us detect dropped audio
                // (e.g. an hour-long recording with only 30 min of packets).
                // All optional for backwards/forwards compatibility.
                byteSize: z.number().nullish(),
                packetCount: z.number().nullish(),
                recordingStartedAt: z.string().nullish(),
                firstPacketAt: z.string().nullish(),
                lastPacketAt: z.string().nullish(),
                // Degraded write-ahead backup: chunking stopped mid-segment,
                // this file is the segment's ONLY complete audio. The server
                // must protect it and supersede the segment's chunk
                // transcripts with it. Optional for compatibility.
                wasDegraded: z.boolean().nullish(),
              }),
            ),
          })
          .optional(),
      }),
      responses: {
        200: z.object({
          reconciled: z.object({
            cancelled: z.number(),
            matched: z.number(),
          }),
          missingFiles: z.array(z.object({ fileName: z.string() })),
        }),
        404: z.object({ message: z.string() }),
      },
      summary:
        'Complete an appointment: reconcile audio slots against the client manifest, then transition to COMPLETED',
    },
  },
  {
    pathPrefix: '/:tenantId/appointments/',
  },
);
